import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { DocumentPlusIcon, MapPinIcon } from "@heroicons/react/24/outline";
import { PMDialog } from "../../utils/PMDialog";
import { DisciplineCategoryChapterSelector } from "./DisciplineCategoryChapterSelector";
import { buildNewDocumentSearchParams } from "../../../utils/documentStructureUtils";

export default function NewHseDocumentDialog({
  open,
  onClose,
  filterContext,
  defaultDisciplineId,
  defaultCategoryId,
  defaultChapterNumber,
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [mode, setMode] = useState("choose");
  const [disciplineId, setDisciplineId] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [chapterNumber, setChapterNumber] = useState("");

  const hasActiveFilter = filterContext?.hasSidebarFilter;

  useEffect(() => {
    if (!open) return;
    setMode(hasActiveFilter ? "choose" : "custom");
    setDisciplineId(defaultDisciplineId || "");
    setCategoryId(defaultCategoryId || "");
    setChapterNumber(defaultChapterNumber || "");
  }, [open, hasActiveFilter, defaultDisciplineId, defaultCategoryId, defaultChapterNumber]);

  const navigateToCreate = ({ discipline, category, chapter } = {}) => {
    const params = buildNewDocumentSearchParams({
      disciplineId: discipline ?? disciplineId,
      categoryId: category ?? categoryId,
      chapterNumber: chapter ?? chapterNumber,
    });
    onClose?.();
    navigate(`/dashboard/hse-ks/documents/new?${params.toString()}`);
  };

  const handleCreateOnFilter = () => {
    navigateToCreate({
      discipline: defaultDisciplineId || undefined,
      category: defaultCategoryId || undefined,
      chapter: defaultChapterNumber || undefined,
    });
  };

  const handleCreateCustom = () => {
    if (!disciplineId || !categoryId) {
      return;
    }
    navigateToCreate();
  };

  const canCreateCustom = Boolean(disciplineId && categoryId);

  return (
    <PMDialog open={open} onClose={onClose} title={t("Nytt dokument")} size="lg">
      <div className="space-y-5 px-1 py-2">
        {hasActiveFilter && mode === "choose" && (
          <>
            <div>
              <p className="text-sm text-gray-600 mb-3">
                {t("Velg hvor det nye dokumentet skal opprettes")}
              </p>

              <button
                type="button"
                onClick={handleCreateOnFilter}
                className="w-full text-left rounded-lg border-2 border-blue-200 bg-blue-50 p-4 hover:border-blue-400 hover:bg-blue-100 transition-colors"
              >
                <div className="flex items-start gap-3">
                  <MapPinIcon className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-blue-900">
                      {t("Opprett på filtrert plassering")}
                    </p>
                    {filterContext?.summaryLabel ? (
                      <p className="text-sm text-blue-800 mt-1 break-words">
                        {filterContext.summaryLabel}
                      </p>
                    ) : (
                      <p className="text-sm text-blue-700 mt-1">
                        {t("Bruk valgt bransje, kategori og kapittel fra sidemenyen")}
                      </p>
                    )}
                  </div>
                </div>
              </button>
            </div>

            <div className="relative">
              <div className="absolute inset-0 flex items-center" aria-hidden="true">
                <div className="w-full border-t border-gray-200" />
              </div>
              <div className="relative flex justify-center">
                <span className="bg-white px-3 text-xs font-medium text-gray-500 uppercase tracking-wide">
                  {t("eller")}
                </span>
              </div>
            </div>

            <button
              type="button"
              onClick={() => setMode("custom")}
              className="w-full text-left rounded-lg border border-gray-200 bg-white p-4 hover:border-gray-300 hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-start gap-3">
                <DocumentPlusIcon className="h-5 w-5 text-gray-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-gray-900">
                    {t("Velg annen plassering")}
                  </p>
                  <p className="text-sm text-gray-600 mt-1">
                    {t("Velg bransje, kategori og kapittel manuelt")}
                  </p>
                </div>
              </div>
            </button>
          </>
        )}

        {(mode === "custom" || !hasActiveFilter) && (
          <>
            {hasActiveFilter && (
              <button
                type="button"
                onClick={() => setMode("choose")}
                className="text-sm text-blue-600 hover:text-blue-800 font-medium"
              >
                ← {t("Tilbake til valg")}
              </button>
            )}

            <div>
              <p className="text-sm text-gray-600 mb-3">
                {hasActiveFilter
                  ? t("Velg bransje, kategori og kapittel for det nye dokumentet")
                  : t("Velg plassering for det nye dokumentet")}
              </p>
              <DisciplineCategoryChapterSelector
                disciplineId={disciplineId}
                categoryId={categoryId}
                chapterNumber={chapterNumber}
                onDisciplineChange={setDisciplineId}
                onCategoryChange={(value) => {
                  setCategoryId(value);
                  setChapterNumber("");
                }}
                onChapterChange={setChapterNumber}
                required
              />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                {t("Avbryt")}
              </button>
              <button
                type="button"
                disabled={!canCreateCustom}
                onClick={handleCreateCustom}
                className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t("Opprett dokument")}
              </button>
            </div>
          </>
        )}
      </div>
    </PMDialog>
  );
}
